import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2, Mail, MessageSquareText, Plus, Radio, Smartphone, Webhook as WebhookIcon, X, XCircle,
} from 'lucide-react'
import { notiApi, type ChannelType, type NotiChannel } from '../lib/api-noti'

export const CHANNEL_META: Record<ChannelType, { label: string; icon: typeof Mail; desc: string }> = {
  email: { label: 'Email (SMTP)', icon: Mail, desc: 'ส่งอีเมลผ่าน SMTP server' },
  sms: { label: 'SMS', icon: MessageSquareText, desc: 'ส่งข้อความผ่าน SMS gateway' },
  fcm: { label: 'Push (FCM)', icon: Smartphone, desc: 'Push notification ผ่าน Firebase Cloud Messaging' },
  webhook: { label: 'API (Webhook)', icon: WebhookIcon, desc: 'ส่งต่อเป็น HTTP POST ไประบบปลายทาง' },
  mock: { label: 'Mock', icon: Radio, desc: 'สำหรับทดสอบ — บันทึก log แทนการส่งจริง' },
}

export default function ChannelsPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const channels = useQuery({ queryKey: ['noti-channels'], queryFn: () => notiApi.listChannels() })

  return (
    <>
      <div className="page-head">
        <h2><Radio size={20} /> Channels</h2>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> สร้าง Channel</button>
      </div>
      <p className="page-desc muted">
        ระบบปลายทางที่ไม่ใช่แอป (Email / SMS / Push / API) — กำหนด configuration การเชื่อมต่อ
        และทดสอบการเชื่อมต่อได้จากที่นี่ workflow อ้างถึง channel เหล่านี้เป็นปลายทางการส่ง
      </p>

      {channels.isError && <div className="panel error">{(channels.error as Error).message}</div>}

      <div className="card-grid">
        {(channels.data?.data ?? []).map((ch) => {
          const meta = CHANNEL_META[ch.type]
          const Icon = meta.icon
          return (
            <Link key={ch.id} to="/channels/$channelId" params={{ channelId: ch.id }} className="channel-card">
              <div className="channel-card-head">
                <span className="channel-icon"><Icon size={20} /></span>
                <span className={`pill ${ch.enabled ? 'ok' : 'warn'}`}>{ch.enabled ? 'เปิดใช้งาน' : 'ปิด'}</span>
              </div>
              <h3>{ch.name}</h3>
              <div className="muted small"><code>{ch.id}</code> · {meta.label}</div>
              <div className="channel-card-foot">
                {ch.lastTestStatus === 'ok' && <span className="test-ok"><CheckCircle2 size={13} /> ทดสอบผ่าน {ago(ch.lastTestAt)}</span>}
                {ch.lastTestStatus === 'failed' && <span className="test-fail"><XCircle size={13} /> ทดสอบไม่ผ่าน {ago(ch.lastTestAt)}</span>}
                {!ch.lastTestStatus && <span className="muted small">ยังไม่เคยทดสอบการเชื่อมต่อ</span>}
              </div>
            </Link>
          )
        })}
      </div>

      {createOpen && (
        <CreateChannelModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false)
            qc.invalidateQueries({ queryKey: ['noti-channels'] })
          }}
        />
      )}
    </>
  )
}

function CreateChannelModal({ onClose, onCreated }: { onClose: () => void; onCreated: (ch: NotiChannel) => void }) {
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [type, setType] = useState<ChannelType>('email')

  const create = useMutation({
    mutationFn: () => notiApi.createChannel({ id, name, type }),
    onSuccess: onCreated,
  })

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>สร้าง Channel ใหม่</h3>
          <button className="icon-ghost" onClick={onClose} aria-label="ปิด"><X size={16} /></button>
        </div>
        <div className="form-grid">
          <label>Channel ID
            <input value={id} onChange={(e) => setId(e.target.value)} placeholder="email-secondary" spellCheck={false} />
          </label>
          <label>ชื่อ
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Secondary SMTP" />
          </label>
          <label>ชนิด
            <select value={type} onChange={(e) => setType(e.target.value as ChannelType)}>
              {(Object.keys(CHANNEL_META) as ChannelType[]).map((t) => (
                <option key={t} value={t}>{CHANNEL_META[t].label}</option>
              ))}
            </select>
            <span className="muted small">{CHANNEL_META[type].desc}</span>
          </label>
        </div>
        {create.isError && <div className="error">{(create.error as Error).message}</div>}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>ยกเลิก</button>
          <button className="btn-primary" disabled={!id || !name || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'กำลังสร้าง…' : 'สร้าง — ไปตั้งค่าการเชื่อมต่อ'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ago(iso?: string) {
  if (!iso) return ''
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'เมื่อสักครู่'
  if (mins < 60) return `${mins} นาทีที่แล้ว`
  if (mins < 60 * 24) return `${Math.round(mins / 60)} ชม.ที่แล้ว`
  return new Date(iso).toLocaleDateString('th-TH')
}
